import {getServicesByInterface, interfaces} from "../constants";

const textToImageServiceIds = getServicesByInterface(interfaces["text-to-image"])
    .map(({serviceId}) => `"${serviceId}"`).join(" | ");

const textToImage = `
    declare namespace openkbs {
        /**
         * Generates an image based on the given prompts and parameters.
         * 
         * @param prompt The input prompt for image generation.
         * @param options Configuration parameters for image generation.
         * @param options.seed number used to initialize the generation. Defaults to random number.
         * @param options.negative_prompt A string or array of strings describing what should not appear in the image.        
         * @param options.num_inference_steps The number of denoising steps. Defaults to 50.
         * @param options.guidance_scale Guidance scale for the generation. Defaults to 5.0.     
         * @param options.modelId diffusion model (stabilityai--stable-diffusion-xl-base-1.0--with-refiner or alternative cagliostrolab--animagine-xl-3.0--default)         
         * @return A promise that resolves with the generated image.
         */
         
        function textToImage(prompt: string, options: {
            service_id?: ${textToImageServiceIds};
            seed?: number;
            negative_prompt?: string | string[];
            num_inference_steps?: number;
            guidance_scale?: number;
        }): Promise<any>;
    }
`;


const webpageToText = `
    declare namespace openkbs {
        /**
         * Converts the content of a webpage into text. Optionally parses for price information.
         * 
         * @param url The URL of the webpage to convert.
         * @param options Configuration options for the conversion.
         * @param options.parsePrice If true, the parser will attempt to extract price information from the webpage.
         * @return A promise that resolves with the text content of the webpage, and optionally price information.
         */
        function webpageToText(url: string, options: { parsePrice?: boolean }): Promise<string>;
    }
`;

const googleSearch = `
    declare namespace openkbs {
        /**
         * Performs a Google search with the specified query and options.
         * 
         * @param query The search query string.
         * @param options Configuration options for the search.
         * @param options.num The number of search results to return. Maximum varies by API plan.
         * @param options.start The first result to retrieve (starting from 1).
         * @param options.lr Language restrict search. Use language codes specified by ISO 639-1.
         * @param options.hl The interface language. Use language codes specified by ISO 639-1.
         * @param options.safe Search safety level. Can be "off", "medium", or "high".
         * @param options.filter Filter duplicate content. Valid values are "0" (off) or "1" (on).
         * @param options.gl Geolocation of end user. Use a country code specified by ISO 3166-1 Alpha-2.
         * @param options.cr The country restrict(s) for the search. Use a country code specified by ISO 3166-1 Alpha-2.
         * @param options.googlehost The domain to use for the Google search. For example, "google.com" or "google.co.uk".
         * @param options.c2coff Turns off the country correction. Use "1" to turn off.
         * @param options.dateRestrict Restricts results to URLs based on date. Use formats like "d[number]", "w[number]", "m[number]", or "y[number]".
         * @param options.exactTerms Finds pages that contain the exact term or terms specified.
         * @param options.excludeTerms Filters results that contain the specified terms.
         * @param options.linkSite Specifies that all search results should contain a link to a particular URL.
         * @param options.relatedSite Finds pages that are related to the specified URL.
         * @param options.orTerms Provides additional terms to search for results that may contain one term or another.
         * @param options.siteSearch Restricts results to those websites in the given domain.
         * @param options.siteSearchFilter Controls whether to include or exclude results from the site named in the siteSearch parameter. Can be "i" (include) or "e" (exclude).
         * @param options.sort Sorts results by a specified condition, such as by date.
         * @param options.imgDominantColor Returns images of a specific dominant color. Acceptable values are: black, blue, brown, gray, green, orange, pink, purple, red, teal, white, yellow
         * @param options.imgColorType Returns black and white, grayscale, transparent, or color images. Acceptable values are: color, gray, mono, trans
         * @param options.imgSize Returns images of a specified size. Acceptable values are: huge, icon, large, medium, small, xlarge, xxlarge    
         * @param options.imgType Returns images of a type. Acceptable values are: clipart, face, lineart, stock, photo, animated    
         * @return A promise that resolves with the search results.
         */
        function googleSearch(query: string, options: {
            num?: number;
            start?: number;
            lr?: string;
            hl?: string;
            safe?: string;
            filter?: string;
            gl?: string;
            cr?: string;
            googlehost?: string;
            c2coff?: string;
            dateRestrict?: string;
            exactTerms?: string;
            excludeTerms?: string;
            linkSite?: string;
            relatedSite?: string;
            orTerms?: string;
            siteSearch?: string;
            siteSearchFilter?: string;
            sort?: string;
            imgColorType?: string;
            imgDominantColor?: string;
            imgSize?: string;
            imgType?: string;
        }): Promise<any>;
    }
`
const documentToText = `
    declare namespace openkbs {
        /**
         * Converts the content of a document at a specified URL to text.        
         * This function supports various document formats, such as PDF, DOCX, etc.
         * 
         * @param url The URL of the document to convert to text.
         * @return A promise that resolves with the text content of the document.
         */
        function documentToText(url: string): Promise<string>;
    }
`

const imageToText = `
    declare namespace openkbs {
        /**
         * Optical character recognition (OCR) - extracts the text from an image at a specified URL
         * 
         * @param url The URL of the image from which to extract text. The image must be accessible
         * over the internet, and the URL should point directly to the image file.
         
         * @param options Configuration options for text extraction.
         * should return only the extracted text (without coordinates)
         * @return A promise that resolves with the extracted text from the image.
         */
        function imageToText(url: string): Promise<string | Array<{text: string, coordinates: any}>>;
    }
`

const detectLanguage = `
    declare namespace openkbs {
        /**
         * Detects the language of the provided text.
         * 
         * @param text The text for which to detect the language.
         * @return A promise that resolves with the detected language code.
         */
        function detectLanguage(text: string): Promise<string>;
    }
`

const translate = `
    declare namespace openkbs {
        /**
         * Translates the provided text into the specified language.
         * 
         * @param text The text to translate.
         * @param targetLang The target language code (ISO 639-1).
         * @return A promise that resolves with the translated text.
         */
        function translate(text: string, targetLang: string): Promise<string>;
    }
`;


const textToSpeech = `
    declare namespace openkbs {
        /**
         * Converts the provided text into speech audio using Google Cloud Text-to-Speech.
         * 
         * @param text The text to be converted into speech.         
         * @param options Configuration options
         * @param options.languageCode (Optional) The language code of the voice, following the BCP-47 standard. Default is 'en-US'.
         * @param options.ssmlGender (Optional) The gender of the voice. Options are 'MALE', 'FEMALE', 'NEUTRAL'. Default is 'NEUTRAL'.
         * @param options.sampleRateHertz (Optional) The sample rate in hertz of the audio. Must be a positive integer.
         * @param options.speakingRate (Optional) The speaking rate of the generated audio. Must be a positive number. 1.0 is the normal speaking rate.
         * @param options.audioEncoding (Optional) The encoding of the audio file. Options are 'LINEAR16', 'MP3', 'OGG_OPUS'. Default is 'MP3'.
         * @param options.name (Optional) The name of the voice. If not set, the API will choose a voice based on the languageCode and ssmlGender.        
         * @return A promise that resolves with the base64 encoded string of the audio content.
         */
        function textToSpeech(text: string, languageCode?: string, ssmlGender?: string, sampleRateHertz?: number, speakingRate?: number, audioEncoding?: string, name?: string, transactionJWT: string): Promise<string>;
    }
`;




export const definitions = [
    textToImage,
    webpageToText,
    googleSearch,
    documentToText,
    imageToText,
    detectLanguage,
    translate,
    textToSpeech
]